import React, { useCallback, useEffect, useState } from "react";
import { Bell, CheckCircle2, Loader2, Lock } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";
import GlassCard from "@/components/GlassCard";

/**
 * EmailNotificationsSection
 * -------------------------
 * Small settings section, mounted on the Business Profile page,
 * that lets a user toggle successful-publish emails. Billing and
 * failure emails are always on and shown as locked.
 *
 * Backend contract:
 *   GET   /api/settings/email-preferences -> {
 *     notify_billing, notify_publish_failure, notify_publish_success,
 *     notify_billing_locked, notify_publish_failure_locked
 *   }
 *   PATCH /api/settings/email-preferences  body { notify_publish_success: bool }
 *
 * UX rules honored:
 *   - Never expose a control for billing or failure emails.
 *   - No opt-in for bulk contact lists.
 *   - On save failure, revert the toggle and show the error.
 *   - Show a small saved confirmation on success.
 */
export default function EmailNotificationsSection() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notifySuccess, setNotifySuccess] = useState(true);
  const [savedTick, setSavedTick] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/settings/email-preferences");
      setNotifySuccess(!!res?.data?.notify_publish_success);
    } catch (_e) {
      // Non-fatal — keep default true, but tell the user we couldn't load
      toast.error("Couldn't load email preferences. Using defaults.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onToggle = async (next) => {
    // Optimistic update; revert on failure.
    const prev = notifySuccess;
    setNotifySuccess(next);
    setSaving(true);
    setSavedTick(false);
    try {
      const res = await api.patch("/settings/email-preferences", {
        notify_publish_success: next,
      });
      if (res?.data && typeof res.data.notify_publish_success === "boolean") {
        setNotifySuccess(res.data.notify_publish_success);
      }
      setSavedTick(true);
      setTimeout(() => setSavedTick(false), 1800);
    } catch (_e) {
      setNotifySuccess(prev);
      toast.error("Couldn't save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <GlassCard className="mt-6 p-5" data-testid="email-notifications-section">
      <div className="flex items-center gap-2">
        <Bell className="h-4 w-4 text-[color:var(--text-2)]" aria-hidden="true" />
        <h3 className="text-sm font-semibold text-[color:var(--text-1)]">
          Email notifications
        </h3>
        {savedTick ? (
          <span
            className="ml-2 inline-flex items-center gap-1 text-[11px] text-[color:var(--success,#10b981)]"
            data-testid="email-notifications-saved"
          >
            <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
            Saved
          </span>
        ) : null}
      </div>

      <p className="text-[12px] text-[color:var(--text-3)] mt-1">
        Choose which account emails you want to receive from Postmoro.
      </p>

      <div className="mt-4 space-y-3">
        {/* Row 1: Membership & billing — locked on */}
        <div
          className="flex items-start justify-between gap-4 p-3 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)]"
          data-testid="email-row-billing"
        >
          <div className="min-w-0">
            <div className="text-sm text-[color:var(--text-1)]">
              Membership and billing updates
            </div>
            <div className="text-[12px] text-[color:var(--text-3)]">
              Essential account and payment notices.
            </div>
          </div>
          <Badge
            variant="secondary"
            className="text-[10px] uppercase tracking-wider inline-flex items-center gap-1 shrink-0"
            data-testid="email-row-billing-status"
          >
            <Lock className="h-3 w-3" aria-hidden="true" />
            Always on
          </Badge>
        </div>

        {/* Row 2: Failed post notifications — locked on */}
        <div
          className="flex items-start justify-between gap-4 p-3 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)]"
          data-testid="email-row-failure"
        >
          <div className="min-w-0">
            <div className="text-sm text-[color:var(--text-1)]">
              Failed post notifications
            </div>
            <div className="text-[12px] text-[color:var(--text-3)]">
              Alerts when Postmoro cannot publish a post.
            </div>
          </div>
          <Badge
            variant="secondary"
            className="text-[10px] uppercase tracking-wider inline-flex items-center gap-1 shrink-0"
            data-testid="email-row-failure-status"
          >
            <Lock className="h-3 w-3" aria-hidden="true" />
            Always on
          </Badge>
        </div>

        {/* Row 3: Successful post notifications — user toggle */}
        <div
          className="flex items-start justify-between gap-4 p-3 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)]"
          data-testid="email-row-success"
        >
          <div className="min-w-0">
            <div className="text-sm text-[color:var(--text-1)]">
              Successful post notifications
            </div>
            <div className="text-[12px] text-[color:var(--text-3)]">
              Email me after a post is successfully published.
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {saving || loading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-[color:var(--text-3)]" aria-hidden="true" />
            ) : null}
            <Switch
              checked={notifySuccess}
              onCheckedChange={onToggle}
              disabled={loading || saving}
              aria-label="Toggle successful post email notifications"
              data-testid="email-row-success-toggle"
            />
          </div>
        </div>
      </div>
    </GlassCard>
  );
}
